import { renderMoment } from '@utils/timeUtils'

// Splits a raw dateTime into display date/time parts (mirrors freebetsMapper.js).
const splitDateTime = (dateTime) => {
  const moment = dateTime
    ? renderMoment({
        formatType: 'DD MMM YYYY',
        utcDateString: dateTime,
        options: { seconds: false, hourFormat: '24', displayMode: 'LOCAL' },
      })
    : null
  return { date: moment?.date ?? '', time: moment?.time ?? '' }
}

// Current jackpot (brand or user-specific) -> render-ready view model.
export const toCurrentJackpotView = (data, { renderValue }) => {
  if (!data) return null

  return {
    id: data.jackpotId,
    name: data.jackpotName,
    filled: data.jackpotFilledStatus,
    maturityValue: renderValue(data.jackpotMaturityValue),
    userContributionPercentage: data.userContributionPercentage ?? null,
  }
}

// User's jackpot tickets -> render-ready view model.
export const toJackpotTicketsView = (data, { formatDate }) => ({
  rows: (data?.userTickets ?? []).map((t) => ({
    date: formatDate(t.dateTime),
    ticketIds: t.ticketIds ?? [],
    totalTickets: t.totalTickets,
  })),
})

// Top contributions (leaderboard + current user) -> render-ready view model.
export const toTopContributionsView = (data) => {
  const block = data?.currentJackpotContributions ?? {}
  const mapEntry = (c) => ({
    userId: c.userId,
    winChance: c.winChance,
    tickets: c.ticketsAllocated,
  })

  // `currentUserJackpotContributions` is a single object on the schema (not a
  // list) — null when the player hasn't contributed to this jackpot yet.
  return {
    id: block.jackpotId,
    name: block.jackpotName,
    top: (block.jackpotTopContributions ?? []).map(mapEntry),
    currentUser: block.currentUserJackpotContributions ? [mapEntry(block.currentUserJackpotContributions)] : [],
  }
}

// Jackpot prize distribution -> render-ready view model.
export const toPrizeDistributionView = (data, { renderValue }) => ({
  rows: (data?.jackpotPrizeDistributions ?? []).map((p) => ({
    position: p.position,
    reward: renderValue(p.rewardAmount),
  })),
})

// User contributions to a jackpot -> render-ready view model.
export const toContributionsView = (data, { renderValue }) => ({
  rows: (data?.userContributions ?? []).map((c) => ({
    ...splitDateTime(c.dateTime),
    type: c.contributionType,
    stake: renderValue(c.stakeAmount),
    contribution: renderValue(c.contributionAmount),
    tickets: c.ticketsAllocated,
    betId: c.betId,
    ticketRange: c.ticketRange ?? null,
  })),
})

// User's jackpot history -> render-ready view model.
export const toJackpotHistoryView = (data, { renderValue, formatDate }) => ({
  rows: (data?.jackpotHistoryDetails ?? []).map((h) => ({
    id: h.jackpotId,
    name: h.jackpotName,
    maturityValue: renderValue(h.jackpotMaturityValue),
    endedDate: formatDate(h.endedDate),
    reward: renderValue(h.userReward),
  })),
})

// Results of a settled jackpot -> render-ready view model.
export const toJackpotHistoryResultsView = (data, { renderValue }) => ({
  rows: (data?.results ?? []).map((r) => ({
    position: r.position,
    userId: r.userId,
    winningTicket: r.winningTicket,
    reward: renderValue(r.rewardAmount),
  })),
})
